/**
 * The `encoding_ladder` contract of the `vod-default-cmaf-abr` VTK template.
 *
 * Mirrors `vtkpy/templates/castlabs_vod/default_cmaf_abr.py`. The ladder is the one part of
 * `format_specific_data` the template *validates*: an unknown key there raises a
 * TemplatingException at render time instead of being silently ignored, so everything this
 * editor emits has to stay inside the key sets below.
 *
 * The legacy `conf` spelling this replaced is now rejected outright rather than ignored --
 * ignoring it would have rendered the default ladder in place of the configured one.
 */

export type LadderCodec = 'h264' | 'h265' | 'av1';
export type LadderOutput = 'dash' | 'hls';
/**
 * `AUDIO` is a tier like the width tiers, and the one that is not selected by width: audio is
 * keyed separately from video, so it carries `max_width: null` and is declared first. The form
 * does not offer it as a choice -- it is always sent -- so it is absent from `DRM_STREAMTYPES`.
 */
export type DrmStreamType = 'AUDIO' | 'SD' | 'HD' | 'HD2' | 'UHD' | 'UHD2';

export interface LadderRendition {
  /** Output width; height follows the source aspect. Also what the DRM tier split reads. */
  width: number;
  /**
   * VBV ceiling, e.g. "2200k". Keys the output filename. Left off, the rung is encoded uncapped and
   * must state its own `avg_bitrate` or `crf` -- which then names the file instead.
   */
  max_bitrate?: string;
  /** Target average. Required on every rendition of a two_pass group that does not state a `crf`. */
  avg_bitrate?: string;
  /**
   * This rung's own constant-quality target, in place of the group's; fractional allowed. Inside a
   * two_pass group such a rung stays a single CRF pass. Excludes `avg_bitrate`.
   */
  crf?: number;
  /** Both need `max_bitrate`: an uncapped rung has no VBV for them to act on. */
  min_bitrate?: string;
  bufsize?: string;
}

export interface LadderGroup {
  codec: LadderCodec;
  renditions: LadderRendition[];
  outputs: LadderOutput[];
  /** Required unless two_pass is set, where it would have no effect. */
  crf?: number;
  two_pass?: boolean;
  profile?: string | null;
  preset?: string | null;
  pixfmt?: string;
  /**
   * Extra `-x265-params` / `-svtav1-params` tuning, e.g. `{sao: 0, 'psy-rd': 1}`. h265 and av1 only;
   * the keys in `LADDER_CODECS[codec].reservedParams` are the template's own and refused.
   */
  encoder_params?: Record<string, string | number>;
}

export interface LadderAudioRendition {
  /** Must be unique across entries -- it distinguishes the output filenames. */
  bitrate: string;
  codec?: string;
  samplerate?: number;
  channels?: number;
}

export interface LadderDrmTier {
  streamtype: DrmStreamType;
  /** Inclusive upper bound of the tier. The last tier must be null (unbounded). */
  max_width: number | null;
}

export interface EncodingLadder {
  groups: LadderGroup[];
  /**
   * Closed-GOP length, and how many GOPs make one CMAF segment.
   *
   * Both are ladder-level, not per group: the packager takes a *single* segment duration for the
   * whole job, so a per-group GOP could never be honoured by it, and a segment that is not a whole
   * number of GOPs would not start on a keyframe. They used to sit on the group -- vtkpy 66d5b4b9
   * moved them here and dropped `gop_in_seconds` from the group key set, so a group still carrying
   * one is now rejected outright rather than ignored.
   */
  gop_in_seconds: number;
  gops_per_segment?: number;
  /**
   * Overrides the per-layout policy behind `auto`. Omitted while it still matches the template's
   * own, so a future change there reaches jobs without a UI release.
   */
  audio_layouts?: LadderAudioLayout[];
  /**
   * One audio set shared by every group, in one of three shapes:
   *
   * * omitted or `"auto"` -- derive the set per track from its own channel layout, using the
   *   template's layout policy (stereo is always delivered, wider layouts are kept when the
   *   source has them, and nothing is ever up-mixed).
   * * `null` -- one output per source track, with no bitrate control at all.
   * * a non-empty list -- exactly these steps, for every track.
   *
   * The UI omits the key for `auto` rather than sending the literal, so a future change to what
   * the template derives reaches jobs without a UI release. That default has already moved twice.
   */
  audio_renditions?: LadderAudioRendition[] | 'auto' | null;
  drm_streamtypes?: LadderDrmTier[];
  exceed_input_resolution?: boolean;
  /**
   * Which manifests an audio-only ladder writes -- present only when `groups` is the explicit
   * empty list. A video ladder states outputs per group instead; the template accepts this key
   * beside `groups` only for the one shape with no group left to carry it (vtkpy 1882f32e).
   */
  outputs?: LadderOutput[];
}

/** Valid `outputs` entries, in the canonical order the template normalises to. */
export const LADDER_OUTPUTS: LadderOutput[] = ['dash', 'hls'];

/**
 * Whether a ladder is the explicit `"groups": []` audio-only shape.
 *
 * A ladder that never states `groups` at all (the flat single-group sugar) is not audio-only --
 * only an *explicit* empty list is, which is what `EncodingLadderComponent` always emits once
 * loaded. Sibling cards (watermark, thumbnails) read this to know a video rendition will not exist.
 */
export function isAudioOnlyLadder(ladder: Pick<EncodingLadder, 'groups'> | null | undefined): boolean {
  return Array.isArray(ladder?.groups) && ladder.groups.length === 0;
}

/** The width tiers, in the order they must be declared. `AUDIO` is not one of them -- see below. */
export const DRM_STREAMTYPES: DrmStreamType[] = ['SD', 'HD', 'HD2', 'UHD', 'UHD2'];

/**
 * The audio key's tier, sent with every ladder and never editable.
 *
 * Every delivery keys its audio, and the template requests the key whether or not the title
 * carries an audio track -- so there is nothing for an operator to decide. It is stated in the
 * ladder rather than left to the template because the stored PO is the only record of the key set
 * a job actually ran with. It is always the first entry: it is unbounded, and a width lookup that
 * read it as a width tier would map every rendition onto the audio key.
 */
export const AUDIO_DRM_TIER: LadderDrmTier = {streamtype: 'AUDIO', max_width: null};

/** The template's default tiers: SD up to 864, HD unbounded above it. */
export const DEFAULT_DRM_STREAMTYPES: LadderDrmTier[] = [
  {streamtype: 'SD', max_width: 864},
  {streamtype: 'HD', max_width: null},
];

export const AUDIO_CODECS = ['aac', 'heaac', 'eac3', 'ac3', 'opus'];

/**
 * Codecs that may only be asked for at two channels.
 *
 * HE-AACv2 carries its second channel as parametric stereo, so it is defined for stereo and
 * nothing else -- a mono rendition would silently degrade to HE-AACv1. The template rejects the pairing
 * at validation (`stereo_only` in its `AUDIO_CODECS` table) rather than guarding it in the filter
 * graph, so the case cannot be built at all; this keeps the form from offering it either.
 */
export const STEREO_ONLY_AUDIO_CODECS = ['heaac'];

/**
 * A blank channel count means "whatever the source is", which the template accepts and cannot
 * check either, so it is allowed. A stated count other than 2 is the case that breaks.
 */
export function audioCodecAllowedAt(codec: string, channels: number | null): boolean {
  return !STEREO_ONLY_AUDIO_CODECS.includes(codec) || channels === null || channels === 2;
}

/**
 * The template's own defaults for the two segment keys, so a ladder read back off an older PO --
 * where the GOP sat on the group -- lands on the same numbers the encoder would have used.
 */
export const DEFAULT_GOP_IN_SECONDS = 4;
export const DEFAULT_GOPS_PER_SEGMENT = 1;

/**
 * What `auto` actually resolves to, so the form can show it rather than describe it.
 *
 * This mirrors `DEFAULT_AUDIO_LAYOUTS` / `DEFAULT_AUDIO_SAMPLERATE` in
 * vtkpy `templates/castlabs_vod/v2/cmaf_abr.py`. It is a copy, not a feed: nothing hands the
 * policy to the browser, so it goes stale silently if the template changes. That has already
 * happened twice — the audio default moved from `null` to a fixed rendition to `auto` inside a single
 * afternoon. `scripts/check-audio-policy.mjs` diffs this table against the template; run it when
 * touching either side.
 */
export interface AudioLayoutPolicy {
  /** Channel count the entry applies to. A track is matched on this, so it is unique per entry. */
  channels: number;
  /** Track-menu marker. Null for stereo, which is what a viewer assumes when nothing says otherwise. */
  display: string | null;
  /**
   * One rendition per codec, each with its own bitrate: codecs are not equally efficient at a given
   * layout, so Opus at 96k sits about where AAC needs 128k. Several entries are what puts 5.1 out
   * in AAC and E-AC-3.
   */
  codecs: {codec: string; bitrate: string}[];
  /** Delivered even when the source is not in this layout (derived by downmix). */
  required: boolean;
  /** Whether this layout may be derived from a wider source. Off means source-only. */
  allow_downmix: boolean;
}

export const AUTO_AUDIO_LAYOUTS: AudioLayoutPolicy[] = [
  {channels: 1, display: 'mono', required: false, allow_downmix: false,
   codecs: [{codec: 'aac', bitrate: '96k'}]},
  {channels: 2, display: null, required: true, allow_downmix: true,
   codecs: [{codec: 'aac', bitrate: '128k'}]},
  {channels: 6, display: '5.1', required: false, allow_downmix: false,
   codecs: [{codec: 'aac', bitrate: '384k'}, {codec: 'eac3', bitrate: '384k'}]},
  {channels: 8, display: '7.1', required: false, allow_downmix: false,
   codecs: [{codec: 'aac', bitrate: '448k'}, {codec: 'eac3', bitrate: '448k'}]},
];

/**
 * One row of the audio form, in every mode.
 *
 * A row is one output rendition: one channel layout in one codec. That is already the shape of an
 * `audio_renditions` entry, and an `audio_layouts` entry collapses to it by repeating the layout
 * once per codec — which is why the three modes can share a single table instead of each having
 * its own. Emission regroups the rows by channel count for `auto`.
 *
 * Which columns apply depends on the mode: `samplerate` is an audio_renditions key, while
 * `display`, `required` and `allow_downmix` only exist on a layout policy.
 */
export interface AudioRow {
  channels: number | null;
  codec: string;
  bitrate: string;
  samplerate: number | null;
  display: string | null;
  required: boolean;
  allow_downmix: boolean;
}

/** Which columns a mode actually writes; the rest are shown disabled so the table stays one shape. */
export const AUDIO_MODE_COLUMNS: Record<AudioMode, (keyof AudioRow)[]> = {
  passthrough_all: ['channels', 'codec', 'bitrate', 'display', 'required', 'allow_downmix'],
  custom_layouts: ['channels', 'codec', 'bitrate', 'display', 'required', 'allow_downmix'],
  custom_renditions: ['channels', 'codec', 'bitrate', 'samplerate'],
};

/** The rows a mode starts from. Passthrough shows one inert row: it configures nothing at all. */
export function defaultAudioRows(mode: AudioMode): AudioRow[] {
  if (mode === 'custom_renditions') {
    return [
      {channels: 2, codec: 'aac', bitrate: '128k', samplerate: AUTO_AUDIO_SAMPLERATE,
       display: null, required: false, allow_downmix: false},
    ];
  }
  // Passthrough all: one row per (layout, codec) pair.
  return AUTO_AUDIO_LAYOUTS.flatMap(layout => layout.codecs.map(entry => ({
    channels: layout.channels,
    codec: entry.codec,
    bitrate: entry.bitrate,
    // Shown, though `auto` does not write it: the template runs every derived rendition at this rate,
    // and hiding it would leave the table claiming less than actually happens.
    samplerate: AUTO_AUDIO_SAMPLERATE,
    display: layout.display,
    required: layout.required,
    allow_downmix: layout.allow_downmix,
  })));
}

/** The ladder key the layouts are written to. Omitted while they still match the template. */
export type LadderAudioLayout = {
  channels: number;
  codecs: {codec: string; bitrate: string}[];
  display?: string | null;
  required?: boolean;
  allow_downmix?: boolean;
};

export const AUTO_AUDIO_SAMPLERATE = 48000;

/**
 * One shape for an `audio_layouts` entry, used by both the editor and the preset comparison.
 *
 * The optional keys are written out rather than left off: a preset and the form have to serialise
 * to the same string for the dropdown to keep naming the preset it started from, and an absent
 * `required` compares differently from an explicit `false`.
 */
export function normaliseAudioLayout(layout: any): LadderAudioLayout {
  return {
    channels: Number(layout.channels),
    display: layout.display ?? null,
    codecs: (layout.codecs ?? []).map((c: any) => ({codec: c.codec, bitrate: c.bitrate})),
    required: !!layout.required,
    allow_downmix: !!layout.allow_downmix,
  };
}

/** What to call a layout in the UI. The track-menu marker is a separate, optional thing. */
export function layoutName(channels: number | null): string {
  return {1: 'Mono', 2: 'Stereo', 6: '5.1', 8: '7.1'}[channels] ?? (channels ? `${channels} ch` : 'Source');
}

/** Display names for the codec ids the policy uses. */
export const AUDIO_CODEC_LABELS: Record<string, string> = {
  aac: 'AAC',
  heaac: 'HE-AACv2',
  eac3: 'E-AC-3',
  ac3: 'AC-3',
  opus: 'Opus',
};

/**
 * How the audio set is decided.
 *
 * "Passthrough all" is the starting point; editing any row turns it into "Custom", the same way
 * editing a ladder turns a preset into Custom. The two custom modes differ in which key they
 * write, and that is not cosmetic: `custom_layouts` is a per-layout policy matched against each
 * track's own channel count, while `custom_renditions` is a static list applied to every track
 * alike. Switching between them silently would change what a job delivers, so an edit keeps
 * whichever form it started from.
 *
 * The old `audio_renditions: null` mode is deliberately absent. It re-encoded every track with no
 * bitrate stated -- the output size was whatever the encoder defaulted to -- while calling itself
 * "one output per source track", which read like a copy. The template still accepts the key for
 * jobs that predate audio configuration; it is simply not offered here.
 */
export type AudioMode = 'passthrough_all' | 'custom_layouts' | 'custom_renditions';

export function isCustomAudioMode(mode: AudioMode): boolean {
  return mode === 'custom_layouts' || mode === 'custom_renditions';
}

/** Which key a mode writes. */
export function audioModeWritesLayouts(mode: AudioMode): boolean {
  return mode !== 'custom_renditions';
}

export const AUDIO_MODES: {id: AudioMode; label: string; description: string}[] = [
  {
    id: 'passthrough_all',
    label: 'Passthrough all',
    description: 'Every source track is carried through in the layout it arrived in, at the bitrate and codec its layout names below. Stereo is delivered on top where a track can be downmixed to it; nothing is ever up-mixed.',
  },
  {
    id: 'custom_layouts',
    label: 'Custom',
    description: 'Edited by hand. One row per layout and codec, each matched against a track\'s own channel count.',
  },
  {
    id: 'custom_renditions',
    label: 'Custom',
    description: 'Edited by hand. Every track gets one output per row, whatever layout it arrived in.',
  },
];

export const PIXEL_FORMATS = ['yuv420p', 'yuv420p10le', 'yuv422p', 'yuv444p'];

/**
 * Per-codec encoder facts, straight off the template's CODECS table.
 *
 * `presets` differ per encoder rather than being cosmetic: libsvtav1 fails to open when handed
 * one of x264's textual preset words, so AV1's are numeric. `twoPass` records whether the
 * encoder can do a real two-pass VBR encode at all -- SVT-AV1 rejects a bitrate ceiling outside
 * CRF mode, so AV1 stays on capped CRF and the template refuses `two_pass` for it.
 */
export const LADDER_CODECS: Record<LadderCodec, {
  label: string;
  encoder: string;
  profiles: string[];
  defaultProfile: string | null;
  presets: string[];
  defaultPreset: string;
  /** The codec's own `pixfmt`, which is NOT `yuv420p` for everything -- see below. */
  defaultPixfmt: string;
  twoPass: boolean;
  /**
   * Whether `encoder_params` applies, and the keys it may not set. Mirrors CODECS `param_flag` /
   * `reserved_params` in the template, which refuses the same keys at render time.
   */
  encoderParams: boolean;
  reservedParams: string[];
  /**
   * The option the encoder resolves an `encoder_params` key to, which is what `reservedParams` is
   * checked against -- CODECS `param_name` in the template. SVT-AV1 matches names exactly; x265
   * accepts other spellings of the same option (`x265ParamName`).
   */
  paramName: (key: string) => string;
  /**
   * Highest CRF the encoder accepts -- CODECS `crf_max` in the template. x264 and x265 stop at 51,
   * SVT-AV1 goes to 63; above that the encoder refuses to open rather than clamping.
   */
  crfMax: number;
}> = {
  h264: {
    label: 'H.264',
    encoder: 'libx264',
    profiles: ['baseline', 'main', 'high'],
    defaultProfile: 'high',
    presets: ['ultrafast', 'superfast', 'veryfast', 'faster', 'fast', 'medium', 'slow', 'slower', 'veryslow'],
    defaultPreset: 'medium',
    // 8-bit deliberately: High 10 has essentially no hardware decode, and H.264 is the codec that
    // has to play on everything.
    defaultPixfmt: 'yuv420p',
    twoPass: true,
    crfMax: 51,
    encoderParams: false,
    reservedParams: [],
    paramName: key => key,
  },
  h265: {
    label: 'HEVC',
    encoder: 'libx265',
    profiles: ['main', 'main10'],
    defaultProfile: 'main10',
    presets: ['ultrafast', 'superfast', 'veryfast', 'faster', 'fast', 'medium', 'slow', 'slower', 'veryslow'],
    defaultPreset: 'medium',
    // 10-bit, and main10 to carry it. Every Apple HEVC decoder is Main10, and 10-bit encoding cuts
    // banding and is slightly *more* efficient even from an 8-bit source, because it stops
    // requantising to 8 bits at each stage.
    defaultPixfmt: 'yuv420p10le',
    twoPass: true,
    crfMax: 51,
    encoderParams: true,
    // X265_RESERVED_PARAMS: each entry is the name x265 resolves to, aliases listed beside it.
    reservedParams: [
      'keyint', 'min-keyint', 'keyint-min', 'scenecut', 'hist-scenecut', 'open-gop',
      'colorprim', 'transfer', 'colormatrix',
      'pools', 'numa-pools', 'frame-threads', 'pass', 'stats', 'fps', 'b-pyramid', 'high-tier',
      'level-idc', 'level', 'profile', 'output-depth', 'uhd-bd',
      'crf', 'qp', 'bitrate', 'vbv-maxrate', 'vbv-bufsize', 'vbv-init', 'lossless',
    ],
    paramName: key => x265ParamName(key),
  },
  av1: {
    label: 'AV1',
    encoder: 'libsvtav1',
    // libsvtav1 rejects the textual profile names, so -profile:v is omitted entirely.
    profiles: [],
    defaultProfile: null,
    // 2 and 3 for the quality-first ladders: preset 2 runs ~2.5x preset 6 (720p rung TDR).
    presets: ['2', '3', '4', '5', '6', '7', '8', '9', '10'],
    defaultPreset: '6',
    // AV1 Main covers 8- and 10-bit, so 10-bit costs no profile change.
    defaultPixfmt: 'yuv420p10le',
    twoPass: false,
    crfMax: 63,
    encoderParams: true,
    // SVTAV1_RESERVED_PARAMS.
    reservedParams: [
      'keyint', 'scd', 'lp', 'irefresh-type', 'pred-struct', 'sframe-dist', 'sframe-mode',
      'color-primaries', 'transfer-characteristics', 'matrix-coefficients',
      'preset', 'profile', 'input-depth', 'level', 'tier',
      'crf', 'cqp', 'qp', 'rc', 'aq-mode', 'tbr', 'mbr', 'buf-sz', 'buf-initial-sz', 'buf-optimal-sz',
      'pass', 'passes', 'stats',
    ],
    paramName: key => key,
  },
};

/**
 * Deepest bit depth each profile can carry. Mirrors `PROFILE_MAX_BIT_DEPTH` in the template.
 *
 * `profile` and `pixfmt` are independent keys that have to agree, and a mismatch does not degrade:
 * the encoder refuses to open ("main profile not supported, internal bit depth 10"). The template
 * checks the pair at render time precisely because the encoder would only fail hours into a job --
 * and this is checked here so it fails while the operator is still looking at it.
 *
 * A maximum is the right shape, not an exact match: a 10-bit-capable profile fed 8-bit samples
 * encodes fine and signals the profile it actually used. Only exceeding the profile fails. AV1 has
 * no entry because no `-profile:v` is emitted for it and Main covers both depths.
 */
export const PROFILE_MAX_BIT_DEPTH: Record<string, Record<string, number>> = {
  h264: {baseline: 8, main: 8, high: 8, high10: 10, high422: 10, high444: 14},
  h265: {main: 8, main10: 10, main12: 12},
};

/** The bit depth a pixel format carries. Absent means "not one this UI offers". */
export const PIXFMT_BIT_DEPTH: Record<string, number> = {
  yuv420p: 8,
  yuv422p: 8,
  yuv444p: 8,
  yuv420p10le: 10,
};

/**
 * Does this profile/pixfmt pair open the encoder? Unknown profiles are not guessed at -- ffmpeg is
 * left to judge those.
 */
export function profileCarriesPixfmt(codec: LadderCodec, profile: string | null, pixfmt: string): boolean {
  const max = profile ? PROFILE_MAX_BIT_DEPTH[codec]?.[profile] : undefined;
  const depth = PIXFMT_BIT_DEPTH[pixfmt];
  return max === undefined || depth === undefined || depth <= max;
}

/** Bitrate strings the encoder accepts: a bare bit count, or one suffixed k / M. */
export const BITRATE_PATTERN = /^\d+[kM]?$/;

/**
 * The option x265's `x265_param_parse` resolves `key` to -- the template's `_x265_param_name`. It
 * skips a leading `--`, reads `_` as `-`, and drops a `no-` or `no` prefix (inverting the value), so
 * `open_gop=1` and `no-open-gop=0` both set open-gop and have to be refused like it.
 */
export function x265ParamName(key: string): string {
  const name = (key.startsWith('--') ? key.slice(2) : key).replace(/_/g, '-');
  if (name.startsWith('no-')) {
    return name.slice(3);
  }
  return name.startsWith('no') ? name.slice(2) : name;
}

/**
 * `encoder_params` as the operator types it: the encoder's own `name=value:name=value` spelling,
 * the same string the template ends up appending to `-x265-params` / `-svtav1-params`.
 */
export function encoderParamsToText(params: Record<string, string | number> | null | undefined): string {
  return Object.entries(params ?? {}).map(([k, v]) => `${k}=${v}`).join(':');
}

/**
 * The inverse of `encoderParamsToText`, or `null` when the text is not a list of `name=value` pairs.
 * Numeric values become numbers so a typed `sao=0` compares equal to a preset's `{sao: 0}`.
 *
 * A value may hold spaces (`tune=fast decode`), as the template's schema allows -- it forbids only
 * `:` and `=`, the two characters that would split the encoder's own params string. Names stay
 * space-free: no encoder option has one, so a space there is a typo, not a parameter.
 */
export function parseEncoderParams(text: string | null | undefined): Record<string, string | number> | null {
  const params: Record<string, string | number> = {};
  // Only a field blank as a whole means "no params". A blank segment inside text (`:`, `sao=0:`)
  // is a slip, and dropping it quietly would submit something other than what was typed.
  const typed = text ?? '';
  if (!typed.trim()) {
    return params;
  }
  for (const pair of typed.split(':').map(p => p.trim())) {
    const match = /^([^=\s]+)=([^=]+)$/.exec(pair);
    if (!match) {
      return null;
    }
    params[match[1]] = /^-?\d+(\.\d+)?$/.test(match[2]) ? Number(match[2]) : match[2];
  }
  return params;
}

export interface LadderPreset {
  id: string;
  label: string;
  description: string;
  ladder: EncodingLadder;
}

/**
 * The two ladders on offer, which are the two ends of the trade-off an operator actually makes.
 *
 * `default` is single-codec H.264 at constant quality: the widest client reach and the best picture
 * per title, at the highest cost per title. It is also what a job that configures nothing renders,
 * stated explicitly here so picking it populates the editor rather than leaving it blank.
 *
 * `bandwidth_saver` is the same content across three codecs under hard bitrate caps: H.264 carries
 * both manifests so every client can play something, HEVC is pinned to HLS and AV1 to DASH, each
 * covering only the renditions where the saving is worth the extra encode. It costs far more encoder
 * time for far less delivered volume.
 *
 * Intermediate ladders are reached by editing either one -- the dropdown is a starting point, not
 * the set of allowed ladders.
 */
export const LADDER_PRESETS: LadderPreset[] = [
  {
    id: 'default',
    label: 'Low Volume / High Quality',
    description: 'Single-pass H.264 at CRF 23, 640 to 1920, with the template\'s own audio default. '
      + 'A constant-quality encode spends whatever a scene needs, so a 1920 rendition is allowed up to '
      + '12000k — fewer titles, each at full quality. This is also exactly what the template renders '
      + 'with no ladder configured.',
    ladder: {
      drm_streamtypes: [
        {streamtype: 'SD', max_width: 864},
        {streamtype: 'HD', max_width: null},
      ],
      gop_in_seconds: 4,
      // Stated rather than inherited: it is the segment length the packager is handed, and the
      // stored PO is the only record of what a job ran with.
      gops_per_segment: 1,
      groups: [
        {
          codec: 'h264',
          profile: 'high',
          preset: 'medium',
          pixfmt: 'yuv420p',
          crf: 23,
          two_pass: false,
          outputs: ['dash', 'hls'],
          renditions: [
            {width: 640, max_bitrate: '1000k'},
            {width: 864, max_bitrate: '2000k'},
            {width: 1280, max_bitrate: '4000k'},
            {width: 1920, max_bitrate: '12000k'},
          ],
        },
      ],
    },
  },
  {
    id: 'bandwidth_saver',
    label: 'Bandwidth Saver',
    description: 'Three codecs at low bitrates, for links that cannot carry the high-quality ladder: '
      + 'H.264 two-pass under hard caps; HEVC (x265 slower, two-pass with a CRF top rung) and AV1 '
      + '(SVT-AV1 preset 2, CRF) with no cap on any rung, so action scenes do not collapse under one; a 4 s segment, and '
      + 'stereo-only audio carrying AAC at 128k beside HE-AACv2 at 64k as switchable alternatives. '
      + 'Pair it with Widevine + FairPlay under Encryption — the DRM set is a per-item setting, so '
      + 'this dropdown cannot change it.',
    // Renders to 29 tasks, 20 of them ffmpeg invocations: 7 two-pass video renditions x 2 passes, the
    // HEVC CRF rung, 3 AV1 renditions, and 2 audio renditions (on an unchunked source). Verified
    // against the template rather than counted by hand.
    ladder: {
      gop_in_seconds: 4,
      // One GOP per segment -- so segment_duration is 4 s, which is what the packager is handed.
      gops_per_segment: 1,
      exceed_input_resolution: false,
      drm_streamtypes: [
        {streamtype: 'SD', max_width: 854},
        {streamtype: 'HD', max_width: null},
      ],
      // A stated list, not the `auto` layout policy: every track gets exactly these two renditions
      // whatever layout it arrived in, so a 5.1 master is downmixed to stereo rather than
      // delivered in 5.1. That is the point on a constrained link. AAC and HE-AACv2 are one
      // *family*, so the two share an HLS group and the player ABRs between them -- dropping to
      // 64k on a bad link instead of stalling.
      audio_renditions: [
        {codec: 'aac', bitrate: '128k', samplerate: 48000, channels: 2},
        {codec: 'heaac', bitrate: '64k', samplerate: 48000, channels: 2},
      ],
      groups: [
        {
          codec: 'h264', profile: 'high', preset: 'slow',
          two_pass: true, outputs: ['dash', 'hls'],
          renditions: [
            {width: 426, avg_bitrate: '250k', max_bitrate: '400k', bufsize: '800k'},
            {width: 640, avg_bitrate: '450k', max_bitrate: '700k', bufsize: '1400k'},
            {width: 854, avg_bitrate: '800k', max_bitrate: '1200k', bufsize: '2400k'},
            {width: 1024, avg_bitrate: '1200k', max_bitrate: '1700k', bufsize: '3000k'},
            {width: 1280, avg_bitrate: '1700k', max_bitrate: '2200k', bufsize: '3500k'},
          ],
        },
        {
          // No profile and no pixfmt: libx265's own defaults, which are main10 at 10 bits. Stating
          // `main` + `yuv420p` here would quietly drop HEVC to 8-bit -- the opposite of the point,
          // since 10-bit is where HEVC keeps gradients clean at these bitrates.
          //
          // No VBV cap anywhere: the 720p rung TDR (VMAF p1/min, not mean) found the 1600k cap was
          // the defect -- it collapsed explosion frames to VMAF ~30 while the mean looked fine.
          // The 1280 rung is uncapped CRF 28.4 (1101 kbps, p1 44.7 -> 63.9); 854/1024 were not
          // retested and stay two-pass at their old averages, minus the cap (the TDR's fixed-size
          // fallback, ~2 p1 points below CRF). `slower` + `sao=0:psy-rd=1` cost ~1.7x `slow`.
          codec: 'h265', preset: 'slower',
          two_pass: true, outputs: ['hls'],
          encoder_params: {'sao': 0, 'psy-rd': 1},
          renditions: [
            {width: 854, avg_bitrate: '500k'},
            {width: 1024, avg_bitrate: '750k'},
            {width: 1280, crf: 28.4},
          ],
        },
        {
          // Likewise 10-bit: AV1 Main covers both depths, so it costs no profile change.
          //
          // SVT-AV1's capped CRF is a hard per-frame clamp, not a VBV buffer: at 1280 it took the
          // worst frames to VMAF ~10. Uncapped CRF 40.5 at preset 2 with variance boost and
          // tf-strength 1 lands at ~1120 kbps with p1 62.7 / min 52.3 (vs 29.0 / 9.8, 35% larger).
          // Preset 2 costs ~2.5x preset 6. No rung is capped: the clamp is the wrong tool at any
          // width. 854/1024 were not retested; they take the same CRF, which on a constant-quality
          // ladder already spends less on fewer pixels (estimated ~600k / ~800k against the old
          // 650k / 950k caps, unmeasured).
          codec: 'av1', preset: '2',
          two_pass: false, outputs: ['dash'],
          encoder_params: {'enable-variance-boost': 1, 'tf-strength': 1},
          renditions: [
            {width: 854, crf: 40.5},
            {width: 1024, crf: 40.5},
            {width: 1280, crf: 40.5},
          ],
        },
      ],
    },
  },
];

export const CUSTOM_PRESET_ID = 'custom';

/** Key-order-independent comparison, so a ladder read back off a stored PO still matches a preset. */
export function stableStringify(value: any): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value ?? null);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value).filter(k => value[k] !== undefined).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}
